import { DateValue } from 'react-aria';
import { getLocalTimeZone, isToday, today } from '@internationalized/date';

export const Environments = {
  VDC: 'vdc',
  RDC: 'rdc',
} as const;

export type Environment = typeof Environments['VDC' | 'RDC'];

export type Status = 'passed' | 'failed' | 'in progress' | 'complete' | 'error' | 'queued';

export interface OwnerItem {
  id: string;
  type: 'user' | 'team' | 'organization';
  name?: string;
}

export interface EnvironmentScopedPayload<T> {
  values: T,
  environment: Environment;
}

export type DateRange = AbsoluteDateRange | RelativeDateRange;

export interface AbsoluteDateRange {
  start: DateValue;
  end: DateValue;
}

export const RelativeDateRanges = {
  today: 'today',
  last7Days: 'last7days',
  last14Days: 'last14days',
  last30Days: 'last30days',
  allTime: 'alltime',
} as const;

export type RelativeDateRange = typeof RelativeDateRanges[keyof typeof RelativeDateRanges];

export function isEnvironment(maybe: unknown): maybe is Environment {
  return typeof maybe === 'string' && (
    maybe === 'vdc' ||
    maybe === 'rdc'
  );
}

export function isStatus(maybe: unknown): maybe is Status {
  return typeof maybe === 'string' && (
    maybe === 'passed' ||
    maybe === 'failed' ||
    maybe === 'in progress' ||
    maybe === 'complete' ||
    maybe === 'error' ||
    maybe === 'queued'
  );
}

export function isStatusList(maybe: unknown): maybe is Status[] {
  return Array.isArray(maybe) && maybe.every((status) => isStatus(status));
}

export function isOwnerItem(maybe: unknown): maybe is OwnerItem {
  return maybe instanceof Object &&
    'id' in maybe &&
    'type' in maybe &&
    'name' in maybe;
}

export function isStringList(maybe: unknown): maybe is string[] {
  return Array.isArray(maybe) && maybe.every((item) => typeof item === 'string');
}

export function isRelativeDateRange(maybe: unknown): maybe is RelativeDateRange {
  return (
    maybe === RelativeDateRanges.today ||
    maybe === RelativeDateRanges.last7Days ||
    maybe === RelativeDateRanges.last14Days ||
    maybe === RelativeDateRanges.last30Days ||
    maybe === RelativeDateRanges.allTime
  );
}

export function toRelativeDateRange(range: DateRange): RelativeDateRange | undefined {
  if (isRelativeDateRange(range)) {
    return range;
  }

  if (isToday(range.start, getLocalTimeZone())) {
    return RelativeDateRanges.today;
  }
  if (range.end.subtract({ days: 6 }).compare(range.start) === 0) {
    return RelativeDateRanges.last7Days;
  }
  if (range.end.subtract({ days: 13 }).compare(range.start) === 0) {
    return RelativeDateRanges.last14Days;
  }
  if (range.end.subtract({ days: 29 }).compare(range.start) === 0) {
    return RelativeDateRanges.last30Days;
  }
  if (range.end.subtract({ days: 89 }).compare(range.start) === 0) {
    return RelativeDateRanges.allTime;
  }
}

export function toCalendarDate(date: RelativeDateRange) {
  switch (date) {
    case RelativeDateRanges.allTime:
      return today(getLocalTimeZone()).subtract({ days: 89 });
    case RelativeDateRanges.last30Days:
      return today(getLocalTimeZone()).subtract({ days: 29 });
    case RelativeDateRanges.last14Days:
      return today(getLocalTimeZone()).subtract({ days: 13 });
    case RelativeDateRanges.last7Days:
      return today(getLocalTimeZone()).subtract({ days: 6 });
    case RelativeDateRanges.today:
    default:
      return today(getLocalTimeZone());
  }
}
